import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import type { DiscoveredPrototype } from '../../shared/discoverPrototypes';
import { CATEGORY_OPTIONS, STATUS_BADGE, formatDate, getCategoryDef, pageKeyOf } from './constants';
import type { Status } from './constants';
import { EDITABLE_STATUSES } from '../../shared/prototypeStatus';

interface PrototypeCardProps {
  proto: DiscoveredPrototype;
  title: string;
  status: Status;
  onChangeStatus: (slug: string, status: Status) => void;
  category: string;
  createdAt: string | null;
  isArchived: boolean;
  isDeleted: boolean;
  protoBasePath: string;
  onChangeCategory: (slug: string, cat: string) => void;
  onChangeTitle: (slug: string, title: string) => void;
  onArchive: (slug: string) => void;
  onUnarchive: (slug: string) => void;
  onDelete: (slug: string) => void;
  onRestore: (slug: string) => void;
  onClick: () => void;
}

const GRADIENTS: Record<string, string> = {
  'general':           'linear-gradient(135deg, #64748b 0%, #94a3b8 100%)',
  'import-export':     'linear-gradient(135deg, #0ea5e9 0%, #38bdf8 100%)',
  'topology':          'linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%)',
  'production':        'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)',
  'plannings':         'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)',
  'optimisations':     'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
  'hors-reseau':       'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
  'autres':            'linear-gradient(135deg, #94a3b8 0%, #cbd5e1 100%)',
  'archive':           'linear-gradient(135deg, #cbd5e1 0%, #e2e8f0 100%)',
};

const BTN_COLORS = {
  rename:  { bg: '#6366f1', text: 'white' },
  cat:     { bg: '#0ea5e9', text: 'white' },
  archive: { bg: '#f59e0b', text: 'white' },
  delete:  { bg: '#ef4444', text: 'white' },
  restore: { bg: '#10b981', text: 'white' },
};

const badgeStyle: React.CSSProperties = {
  display: 'inline-block', fontSize: 10, fontWeight: 700, padding: '3px 8px',
  borderRadius: 6, textTransform: 'uppercase', letterSpacing: '0.4px',
  boxShadow: '0 1px 2px rgba(0,0,0,0.12)',
};

const PREVIEW_MODS = import.meta.glob<{ default: string }>(
  '../../prototypes/*/*/preview.png',
  { eager: true }
);

export default function PrototypeCard({
  proto, title, status, onChangeStatus, category, createdAt, isArchived, isDeleted, protoBasePath,
  onChangeCategory, onChangeTitle, onArchive, onUnarchive, onDelete, onRestore, onClick,
}: PrototypeCardProps) {
  const badge      = STATUS_BADGE[status];
  const catDef     = getCategoryDef(category);
  const gradient   = GRADIENTS[pageKeyOf(category)] ?? GRADIENTS['autres'];
  const previewSrc = PREVIEW_MODS[`../../prototypes/${proto.context}/${proto.slug}/preview.png`]?.default ?? null;

  const [hovered, setHovered]             = useState(false);
  const [editingTitle, setEditingTitle]   = useState(false);
  const [editingCat, setEditingCat]       = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [titleDraft, setTitleDraft]       = useState(title);
  const catSelectRef = useRef<HTMLSelectElement>(null);

  useEffect(() => { setTitleDraft(title); }, [title]);
  useEffect(() => { if (editingCat) catSelectRef.current?.focus(); }, [editingCat]);
  useEffect(() => { if (!hovered) setConfirmDelete(false); }, [hovered]);

  const commitTitle = () => { onChangeTitle(proto.slug, titleDraft); setEditingTitle(false); };

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: isDeleted ? '#fff5f5' : isArchived ? '#f8fafc' : 'white',
        borderRadius: 14,
        border: `1px solid ${isDeleted ? '#fecaca' : hovered && !isArchived ? '#c7d2fe' : '#e2e8f0'}`,
        overflow: 'hidden',
        display: 'flex', flexDirection: 'column',
        transition: 'box-shadow 0.2s, transform 0.2s, border-color 0.2s',
        boxShadow: hovered && !isArchived && !isDeleted ? '0 8px 24px rgba(99,102,241,0.2)' : '0 1px 4px rgba(0,0,0,0.2)',
        transform: hovered && !isArchived && !isDeleted ? 'translateY(-3px)' : 'none',
        opacity: isDeleted ? 0.65 : isArchived ? 0.7 : 1,
        position: 'relative',
      }}
    >
      {/* Statut — hors du lien pour pouvoir le modifier sans ouvrir le prototype */}
      <div style={{ position: 'absolute', top: 10, right: 10, zIndex: 2 }}>
        {isArchived || isDeleted ? (
          <span style={{ ...badgeStyle, background: badge.bg, color: badge.fg }}>{badge.label}</span>
        ) : (
          <label style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
            <span className="sr-only" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>Statut</span>
            <select
              aria-label={`Statut de ${title}`}
              value={status}
              onChange={(e) => onChangeStatus(proto.slug, e.target.value as Status)}
              title="Changer le statut"
              style={{
                ...badgeStyle, background: badge.bg, color: badge.fg,
                appearance: 'none', WebkitAppearance: 'none', border: 'none', cursor: 'pointer',
                paddingRight: 20, fontFamily: 'inherit',
              }}
            >
              {EDITABLE_STATUSES.map((s) => <option key={s} value={s}>{STATUS_BADGE[s].label}</option>)}
            </select>
            <span className="material-symbols-outlined" aria-hidden="true"
              style={{ position: 'absolute', right: 3, fontSize: 14, color: badge.fg, pointerEvents: 'none' }}>
              expand_more
            </span>
          </label>
        )}
      </div>

      {/* ── Thumbnail ── */}
      <Link to={`${protoBasePath}/${proto.slug}`} onClick={onClick}
        style={{ display: 'block', textDecoration: 'none', flexShrink: 0, position: 'relative' }}
        draggable={false}>
        <div style={{ height: 148, position: 'relative', background: previewSrc ? '#f1f5f9' : gradient, overflow: 'hidden' }}>
          {previewSrc ? (
            <img src={previewSrc} alt={`Aperçu — ${title}`}
              style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top left',
                transition: 'transform 0.3s', transform: hovered ? 'scale(1.03)' : 'scale(1)' }} />
          ) : (
            <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span className="material-symbols-outlined" style={{ fontSize: 44, color: 'rgba(255,255,255,0.5)' }}>
                {catDef?.icon ?? 'folder'}
              </span>
            </div>
          )}

          {/* Action buttons — slide up from bottom on hover */}
          <div
            style={{
              position: 'absolute', bottom: 0, left: 0, right: 0,
              background: 'linear-gradient(to top, rgba(0,0,0,0.72) 0%, transparent 100%)',
              padding: '24px 10px 10px',
              display: 'flex', gap: 6, justifyContent: 'center', alignItems: 'center',
              opacity: hovered ? 1 : 0,
              transform: hovered ? 'translateY(0)' : 'translateY(6px)',
              transition: 'opacity 0.2s, transform 0.2s',
              pointerEvents: hovered ? 'auto' : 'none',
            }}
            onClick={(e) => e.preventDefault()}
          >
            {isDeleted ? (
              <IconBtn color={BTN_COLORS.restore} icon="restore_from_trash" title="Restaurer"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onRestore(proto.slug); }} />
            ) : confirmDelete ? (
              <>
                <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.85)', fontWeight: 600 }}>Supprimer ?</span>
                <IconBtn color={BTN_COLORS.delete} icon="check" title="Confirmer"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDelete(proto.slug); }} />
                <IconBtn color={{ bg: 'rgba(255,255,255,0.2)', text: 'white' }} icon="close" title="Annuler"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setConfirmDelete(false); }} />
              </>
            ) : (
              <>
                <IconBtn color={BTN_COLORS.rename} icon="edit" title="Renommer"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setEditingTitle(true); }} />
                <IconBtn color={BTN_COLORS.cat} icon="folder_open" title="Changer la catégorie"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setEditingCat(true); }} />
                <IconBtn
                  color={BTN_COLORS.archive}
                  icon={isArchived ? 'unarchive' : 'inventory_2'}
                  title={isArchived ? 'Désarchiver' : 'Archiver'}
                  onClick={(e) => {
                    e.preventDefault(); e.stopPropagation();
                    isArchived ? onUnarchive(proto.slug) : onArchive(proto.slug);
                  }}
                />
                <IconBtn color={BTN_COLORS.delete} icon="delete" title="Mettre à la corbeille"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setConfirmDelete(true); }} />
              </>
            )}
          </div>
        </div>
      </Link>

      {/* ── Body ── */}
      <div style={{ padding: '14px 16px 16px', flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {editingTitle ? (
          <input
            autoFocus
            value={titleDraft}
            onChange={(e) => setTitleDraft(e.target.value)}
            onBlur={commitTitle}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitTitle();
              else if (e.key === 'Escape') { setTitleDraft(title); setEditingTitle(false); }
            }}
            style={{
              fontSize: 14, fontWeight: 600, padding: '4px 8px',
              border: '2px solid #6366f1', borderRadius: 6,
              width: '100%', fontFamily: 'inherit', color: '#0f172a',
              background: 'white', outline: 'none',
              boxShadow: '0 0 0 3px rgba(99,102,241,0.15)',
            }}
          />
        ) : (
          <Link to={`${protoBasePath}/${proto.slug}`} onClick={onClick}
            style={{ fontSize: 14, fontWeight: 600, color: '#0f172a', textDecoration: 'none', lineHeight: '1.35' }}>
            {title}
          </Link>
        )}

        {proto.meta.description && (
          <p style={{
            margin: 0, fontSize: 12, color: '#64748b', lineHeight: '1.45',
            display: '-webkit-box', WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>
            {proto.meta.description}
          </p>
        )}

        {proto.meta.tags && proto.meta.tags.length > 0 && (
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {proto.meta.tags.slice(0, 3).map((tag) => (
              <span key={tag} style={{
                background: '#f1f5f9', color: '#475569',
                fontSize: 10, fontWeight: 500, padding: '2px 7px', borderRadius: 4,
                border: '1px solid #e2e8f0',
              }}>
                {tag}
              </span>
            ))}
          </div>
        )}

        <div style={{ marginTop: 'auto', paddingTop: 8, borderTop: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          {editingCat ? (
            <select
              ref={catSelectRef}
              value={category}
              onChange={(e) => { onChangeCategory(proto.slug, e.target.value); setEditingCat(false); }}
              onBlur={() => setEditingCat(false)}
              style={{ fontSize: 11, padding: '3px 6px', borderRadius: 6, border: '2px solid #0ea5e9', fontFamily: 'inherit', outline: 'none' }}
            >
              {CATEGORY_OPTIONS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          ) : (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              background: `${catDef?.color ?? '#94a3b8'}18`,
              border: `1px solid ${catDef?.color ?? '#94a3b8'}28`,
              borderRadius: 6, padding: '3px 8px',
              fontSize: 11, color: catDef?.color ?? '#64748b', fontWeight: 500,
            }}>
              <span className="material-symbols-outlined" style={{ fontSize: 12 }}>{catDef?.icon ?? 'folder'}</span>
              {catDef?.label ?? category}
            </span>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#94a3b8' }}>
            {proto.meta.author && (
              <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                <span className="material-symbols-outlined" style={{ fontSize: 12 }}>person</span>
                {proto.meta.author.split('.')[0]}
              </span>
            )}
            {createdAt && <span>{formatDate(createdAt)}</span>}
          </div>
        </div>
      </div>
    </div>
  );
}

function IconBtn({ color, icon, title, onClick }: {
  color: { bg: string; text: string };
  icon: string; title: string;
  onClick: (e: React.MouseEvent) => void;
}) {
  const [hov, setHov] = useState(false);
  return (
    <button
      type="button" title={title} onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        width: 34, height: 34,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        borderRadius: 8, border: 'none', cursor: 'pointer', flexShrink: 0,
        background: hov ? color.bg : 'rgba(255,255,255,0.15)',
        color: hov ? color.text : 'rgba(255,255,255,0.9)',
        backdropFilter: 'blur(4px)',
        transition: 'background 0.15s, color 0.15s, transform 0.1s',
        transform: hov ? 'scale(1.1)' : 'scale(1)',
      }}
    >
      <span className="material-symbols-outlined" style={{ fontSize: 16, lineHeight: 1 }}>{icon}</span>
    </button>
  );
}
